import { lazy, Suspense } from "react"
import { AuthProvider } from "@/contexts/AuthContext"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { Toaster } from "@/components/ui/toast"
import ProtectedRoute from "@/components/ProtectedRoute"

const LoginPage = lazy(() => import("@/pages/LoginPage"))
const LogoutPage = lazy(() => import("@/pages/LogoutPage"))
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"))
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"))
const DashboardPage = lazy(() => import("@/pages/DashboardPage"))
const SettingsPage = lazy(() => import("@/pages/SettingsPage"))
const UsersListPage = lazy(() => import("@/pages/UsersListPage"))
const UserProfilePage = lazy(() => import("@/pages/UserProfilePage"))
const MotoBrandsListPage = lazy(() => import("@/pages/MotoBrandsListPage"))
const MotoBrandDetailPage = lazy(() => import("@/pages/MotoBrandDetailPage"))
const ActionPricesListPage = lazy(() => import("@/pages/ActionPricesListPage"))
const ActionPriceDetailPage = lazy(() => import("@/pages/ActionPriceDetailPage"))
const PromoCampaignsPage = lazy(() => import("@/pages/PromoCampaignsPage"))
const PlansListPage = lazy(() => import("@/pages/PlansListPage"))
const PlanDetailPage = lazy(() => import("@/pages/PlanDetailPage"))
const CompaniesListPage = lazy(() => import("@/pages/CompaniesListPage"))
const CompanyDetailPage = lazy(() => import("@/pages/CompanyDetailPage"))
const AdsListPage = lazy(() => import("@/pages/AdsListPage"))
const AdDetailPage = lazy(() => import("@/pages/AdDetailPage"))
const AdRemovalFeedbackPage = lazy(() => import("@/pages/AdRemovalFeedbackPage"))
const VehicleSpecReferencesPage = lazy(() => import("@/pages/VehicleSpecReferencesPage"))
const ServiceProvidersListPage = lazy(() => import("@/pages/ServiceProvidersListPage"))
const ServiceReviewsModerationPage = lazy(() => import("@/pages/ServiceReviewsModerationPage"))
const MotoGearCategoriesListPage = lazy(() => import("@/pages/MotoGearCategoriesListPage"))
const MotoGearCategoryDetailPage = lazy(() => import("@/pages/MotoGearCategoryDetailPage"))

const routeFallback = (
  <div
    className="flex min-h-screen items-center justify-center text-sm text-muted-foreground"
    role="status"
  >
    Loading...
  </div>
)

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster />
        <Suspense fallback={routeFallback}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/users" element={<UsersListPage />} />
              <Route path="/users/:id" element={<UserProfilePage />} />
              <Route path="/moto-brands" element={<MotoBrandsListPage />} />
              <Route path="/moto-brands/:id" element={<MotoBrandDetailPage />} />
              <Route path="/action-prices" element={<ActionPricesListPage />} />
              <Route path="/action-prices/:id" element={<ActionPriceDetailPage />} />
              <Route path="/promo-campaigns" element={<PromoCampaignsPage />} />
              <Route path="/plans" element={<PlansListPage />} />
              <Route path="/plans/:id" element={<PlanDetailPage />} />
              <Route path="/companies" element={<CompaniesListPage />} />
              <Route path="/companies/:slug" element={<CompanyDetailPage />} />
              <Route path="/ads" element={<AdsListPage />} />
              <Route path="/ads/:id" element={<AdDetailPage />} />
              <Route path="/ad-removal-feedback" element={<AdRemovalFeedbackPage />} />
              <Route path="/vehicle-spec-references" element={<VehicleSpecReferencesPage />} />
              <Route path="/service-providers" element={<ServiceProvidersListPage />} />
              <Route path="/service-reviews" element={<ServiceReviewsModerationPage />} />
              <Route path="/moto-gear-categories" element={<MotoGearCategoriesListPage />} />
              <Route path="/moto-gear-categories/:id" element={<MotoGearCategoryDetailPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  )
}
